// English dictionary (default / fallback language)
const en = {
  common: {
    backToHome: 'Back to home',
  },
  nav: {
    website: 'About',
    createSecurePaper: 'Create SecurePaper',
    decryptSecurePaper: 'Decrypt SecurePaper',
    history: 'History',
    keys: 'Keys',
    logout: 'Log Out',
    login: 'Log In',
    signup: 'Sign Up',
    toggleMenu: 'Toggle menu',
    userMenu: 'User menu',
    user: 'User',
    language: 'Language',
  },
  legal: {
    canonicalNote: 'The canonical, always-current version of this document is published on our website.',
    openFull: 'Read the full document',
    privacyTitle: 'Privacy Policy',
    termsTitle: 'Terms and Conditions',
    securityTitle: 'Security Model',
    securityNote: 'Exactly how SecurePaper protects your data - client-side AES-256-GCM encryption, what is stored where, the full data format, and the offline decryptor - is documented on our website.',
  },
  footer: {
    tagline: 'Secure your sensitive information with paper-friendly encryption',
    quickLinks: 'Quick Links',
    home: 'Home',
    createPaper: 'Create Paper',
    myPapers: 'My Papers',
    myKeys: 'My Keys',
    information: 'Information',
    securityModel: 'Security Model',
    privacyPolicy: 'Privacy Policy',
    terms: 'Terms of Service',
    contact: 'Contact Us',
    howItWorks: 'How It Works & FAQ',
    offlineDecryptor: 'Offline Decryptor',
    website: 'About SecurePaper',
    copyright: '© {year} SecurePaper. All rights reserved.',
    disclaimer: 'Your information is encrypted with AES-256 in your browser -',
    seeHow: 'see how',
  },
  dashboard: {
    title: 'Your SecurePaper Dashboard',
    welcomeBack: 'Welcome back, {email}!',
    subtitle: 'Create and manage your encrypted paper documents securely',
    createNew: 'Create New SecurePaper',
    createNewDesc: 'Encrypt new sensitive information',
    scan: 'Scan SecurePaper',
    scanDesc: 'Decrypt existing documents',
    popular: 'Popular:',
    noAccountStrong: "You're using SecurePaper without an account.",
    noAccountInfo: 'Your keys are saved locally in this browser.',
  },
  templates: {
    'seed-phrase': {
      name: 'Wallet seed phrase',
      subject: 'Crypto wallet seed phrase',
      content: 'Wallet name: \nWallet type (hardware/software): \n\nSeed phrase (words in order):\n1. \n2. \n3. \n4. \n5. \n6. \n7. \n8. \n9. \n10. \n11. \n12. \n\nPassphrase (25th word), if any: ',
    },
    '2fa-codes': {
      name: '2FA backup codes',
      subject: '2FA backup codes',
      content: 'Service: \nAccount / email: \n\nBackup codes:\n- \n- \n- \n- \n- \n',
    },
    'emergency-access': {
      name: 'Emergency access',
      subject: 'Emergency access for my family',
      content: 'If something happens to me, this gives you access to what matters.\n\nPassword manager: \nMaster password: \nPhone PIN: \nEmail account + password: \nBank access: \n\nPeople to contact:\n- \n\nNotes: ',
    },
    'bank-details': {
      name: 'Banking details',
      subject: 'Banking details',
      content: 'Bank: \nLogin: \nPassword: \nCard PIN: \nAccount number: \n\nNotes: ',
    },
    wifi: {
      name: 'Wi-Fi & home codes',
      subject: 'Home network and codes',
      content: 'Wi-Fi network: \nWi-Fi password: \nRouter admin password: \nAlarm code: \nSafe combination: \n\nNotes: ',
    },
  },
  newTextbox: {
    title: 'Create SecurePaper',
    subtitle: 'Convert sensitive written information into encrypted SecurePaper codes for safe paper storage',
    step1: 'Enter information',
    step2: 'Generated SecurePaper',
    startFromTemplate: 'Start from a template',
    optional: '(optional)',
    paperName: 'Paper Name',
    paperNamePlaceholder: 'e.g., Home Safe Combination or Banking Details (optional)',
    protectionMethod: 'Protection method',
    encryptionKeyOption: 'Encryption key',
    encryptionKeyOptionDesc: 'A random key is generated and saved {location}. Strongest option — nothing to remember.',
    savedToAccount: 'to your account',
    savedInBrowser: 'in this browser',
    passphraseOption: 'Passphrase',
    passphraseOptionDesc: 'You choose a passphrase and remember it. Nothing is stored anywhere — paper + your memory is the complete backup.',
    passphraseLabel: 'Passphrase',
    passphrasePlaceholder: 'At least 8 characters — longer is stronger',
    repeatPassphrase: 'Repeat passphrase',
    repeatPassphrasePlaceholder: 'Type the same passphrase again',
    passphraseTooShort: 'The passphrase must be at least 8 characters long',
    passphraseMismatch: 'The passphrases do not match',
    passphraseWarning: 'There is no way to recover a forgotten passphrase — the paper becomes unreadable without it.',
    encryptionKeyLabel: 'Encryption Key',
    createNewKey: 'Create New Key',
    createNewKeyDesc: 'Generate a new secure encryption key',
    useExistingKey: 'Use Existing Key',
    chooseFromKeys: 'Choose from your saved keys ({count})',
    localSuffix: '({count} local)',
    accountLocalSuffix: '({account} account, {local} local)',
    selectKey: '-- Select a key --',
    accountKeys: 'Account Keys',
    localKeys: 'Local Keys',
    keyCreatedOn: 'Key created on {date}',
    localKeyCreatedOn: 'Local key created on {date}',
    localTag: '(Local)',
    loadingKeys: 'Loading keys...',
    contentToSecure: 'Content to Secure',
    contentPlaceholder: 'Enter the sensitive information you would normally write on paper...',
    contentHint: 'This content will be encrypted and converted to a SecurePaper code',
    charCount: '{count} characters',
    qrCodesOne: '≈ 1 QR code',
    qrCodesFew: '≈ {count} QR codes',
    qrCodesMany: '≈ {count} QR codes',
    longContentWarning: 'Long content means many QR codes to print and scan back ({count} codes). Consider splitting it into several shorter papers.',
    generating: 'Generating...',
    generate: 'Generate SecurePaper',
    info1Title: 'Superior to Handwritten Notes',
    info1Desc: 'Your information is encrypted with AES-256 in your browser, unlike vulnerable written notes',
    info2Title: 'Print and Store Safely',
    info2Desc: 'Generate SecurePaper codes you can print and store physically - much safer than plain text',
  },
  result: {
    ready: 'Your SecurePaper is ready',
    readySubtitle: 'Your information has been encrypted and converted to a SecurePaper code',
    stepInfoEntered: 'Information entered',
    stepGenerated: 'Generated SecurePaper',
    yourSecurePaper: 'Your SecurePaper',
    download: 'Download SecurePaper Code',
    print: 'Print Document',
    howToAccess: 'To access the information:',
    goDecrypt: 'Click decrypt SecurePaper page',
    scanPrinted: 'Scan the printed SecurePaper code',
    passphraseStep: 'Enter the passphrase you chose - nothing else is needed',
    loggedInKeysStep: 'Use your saved encryption keys or scan/upload a key',
    localKeysStep: 'Use your locally saved encryption keys or scan/upload a key',
    emergencyBackup: 'Emergency backup:',
    downloadOfflineDecryptor: 'download the offline decryptor',
    offlineDecryptorDesc: '- a single file that decrypts your papers even without our servers',
    printingGuideLink: 'Printing & storage guide - how to keep papers and keys safe for years',
    passphraseAlert1: 'This document is protected with your passphrase.',
    passphraseAlertStrong: 'Nothing was stored anywhere',
    passphraseAlert2: '- the printed paper plus your passphrase is the complete backup. If you forget the passphrase, the paper cannot be decrypted.',
    notLoggedInWarning: "Since you're not logged in, your encryption key is saved locally in your browser.",
    notLoggedInWarningStrong: 'Create an account to sync your keys to the cloud and access them from any device.',
    usingExistingAccount: 'Using your existing encryption key from your account.',
    keySavedAccount: 'Your encryption key is saved to your account. You can access it anytime from your dashboard.',
    usingExistingLocal: 'Using your existing locally saved encryption key.',
    keySavedLocal: 'Your encryption key is saved locally.',
    signupLink: 'Sign up',
    signupToSync: 'to sync it to your account.',
    encryptionKeySection: 'Encryption Key',
    hide: 'Hide',
    show: 'Show',
    saving: 'Saving...',
    saveToAccount: 'Save to account',
    saveLocally: 'Save locally',
    copyKey: 'Copy key',
    copiedKey: 'Copied!',
    copyKeyTitle: 'Copy the encryption key to your clipboard',
    statusUsingExistingAccount: 'Using existing encryption key from your account',
    statusUsingExistingLocal: 'Using existing encryption key saved locally',
    statusSavedAccount: 'Encryption key saved to your account',
    statusSavedLocal: 'Encryption key saved locally',
    createAnother: 'Create Another Document',
    goToDashboard: 'Go to Dashboard',
    notifSavedAccount: 'Message and key saved to your account!',
    notifKeySavedLocally: 'Encryption key saved locally!',
    notifMessageSaved: 'Message saved successfully!',
    notifDownloaded: 'SecurePaper code downloaded!',
    notifPdfCreated: 'PDF document created successfully!',
    keyForName: 'Key for {subject}',
    untitledMessage: 'Untitled Message',
  },
  scan: {
    title: 'Scan SecurePaper',
    subtitle: 'Decrypt your secure information from SecurePaper codes',
    step1: 'Scan SecurePaper Code',
    step2: 'Decrypt Content',
    scanYour: 'Scan Your SecurePaper',
    scanInstructions: 'Scan the SecurePaper code from your SecurePaper document:',
    multiPartTitle: 'Scanning Multi-Part Code',
    multiPartInstr: 'Scan all {total} parts to decrypt',
    partsScanned: '{scanned} of {total} parts scanned',
    part: 'Part {num}',
    initCamera: 'Initializing camera...',
    scanningAuto: 'Scanning automatically...',
    switchCamera: 'Switch Camera',
    cancel: 'Cancel',
    useCamera: 'Use Camera',
    useCameraDesc: "Scan the SecurePaper code using your device's camera",
    startCamera: 'Start Camera',
    uploadImage: 'Upload Image',
    uploadImageDesc: 'Upload an image containing the SecurePaper code',
    processing: 'Processing image...',
    orPasteCode: '— or paste the code as text —',
    pasteCodePlaceholder: 'Paste the SecurePaper code text here (for multi-part codes paste every part, each starting with "SP:")',
    continuePasted: 'Continue with Pasted Code',
    decryptYourContent: 'Decrypt Your Content',
    scanningForKey: 'Scanning for Encryption Key',
    scanningForKeyAuto: 'Scanning for key automatically...',
    tryingSavedKeys: 'Trying to decrypt with your saved keys...',
    passphraseProtected: 'Passphrase-protected document',
    passphraseProtectedDesc: 'This SecurePaper was protected with a passphrase. Enter it to reveal the content - no key file is needed.',
    enterPassphrasePlaceholder: 'Enter the passphrase',
    decryptContent: 'Decrypt Content',
    enterKey: 'Enter Encryption Key',
    enterKeyDesc: 'Provide the encryption key to reveal your content',
    scanKey: 'Scan SecurePaper Key',
    scanKeyDesc: 'Use your camera to scan a SecurePaper key code',
    uploadKeyFile: 'Upload Key File',
    uploadKeyFileDesc: 'Upload a file containing your encryption key',
    keyLoaded: 'Key loaded',
    orPasteKey: '— or paste your key as text —',
    pasteKeyPlaceholder: 'Paste your encryption key here (64 hex characters, or legacy 3f8a...iv...9c2e)',
    keyLoadedPrefix: 'Key loaded: ',
    decryptionSuccessful: 'Decryption Successful',
    decryptedContent: 'Decrypted Content',
    copied: 'Copied!',
    copy: 'Copy',
    decryptNewCode: 'Decrypt New Code',
    backToDashboard: 'Back to Dashboard',
    scannedKeyName: 'Scanned Key - {date}',
    errNoQrDetected: 'No SecurePaper code detected in the image.',
    errKeySplit: 'Encryption keys cannot be split into multiple codes. Please upload a single key code.',
    errPartsMissing: 'Found {found} of {total} parts in image. {missing} part(s) still missing.',
    errProcessImage: 'Failed to process image: {message}',
    errUnknown: 'Unknown error',
    errProvideContentFirst: 'Please provide the SecurePaper content first',
    errEnterPassphrase: 'Please enter the passphrase for this document',
    errProvideBoth: 'Please provide both SecurePaper content and encryption key',
    errInvalidKeyFormat: 'Invalid encryption key format. Expected 64 hex characters, or the legacy form <key>iv<iv>',
    errPastedPartsMissing: 'This code has {total} parts but only {pasted} were pasted. Please paste all parts (each starts with "SP:").',
    errCameraNotReady: 'Camera not ready. Please wait for camera to initialize.',
    errVideoNotReady: 'Video not ready. Please wait a moment and try again.',
    errCaptureFailed: 'Failed to capture image from camera.',
    errCaptureFailedWith: 'Failed to capture image: {message}',
    errInitCameraFailed: 'Failed to initialize camera. Please try again.',
    errPlaybackFailed: 'Failed to start video playback: {message}',
    errCameraAccess: 'Failed to access camera. ',
    errAllowPermissions: 'Please allow camera permissions and try again.',
    errNoCamera: 'No camera found on this device.',
    errCameraInUse: 'Camera is already in use by another application.',
    errUnknownOccurred: 'Unknown error occurred.',
  },
  login: {
    title: 'Sign in to SecurePaper',
    email: 'Email address',
    emailRequired: 'Email is required',
    emailInvalid: 'Email is invalid',
    password: 'Password',
    passwordRequired: 'Password is required',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign in',
    or: 'or',
    continueWithGoogle: 'Continue with Google',
    createAccountLink: 'Create an account.',
    unexpectedError: 'Unexpected error',
    emailNotVerified1: 'Your email is not verified. Click the link inside the welcome email or click',
    emailNotVerifiedHere: 'here',
    emailNotVerified2: ' to resend.',
  },
  signup: {
    title: 'Please sign up',
    checkEmail: 'Check your email',
    sentConfirmationBefore: 'We sent a confirmation link to',
    activateInfo: "Click the link in that email to activate your account. If you don't see it, check your spam folder.",
    goToLogin: 'Go to login',
    emailNote: 'You will occasionally receive account related emails. We promise not to share your email with anyone.',
    passwordHint: 'Use at least one lowercase letter, one numeral, and seven characters.',
    passwordMin: 'Password must be at least 7 characters',
    passwordLower: 'Password must contain at least one lowercase letter',
    passwordNum: 'Password must contain at least one numeral',
    emailExists: 'An account with this email already exists. Try logging in instead.',
    agreePrefix: 'By clicking on "Create an account" below, you are agreeing to the',
    termsLink: 'Terms and Conditions',
    privacyLink: 'Privacy Policy',
    createAccount: 'Create an account',
    signUpWithGoogle: 'Sign up with Google',
  },
  listCodes: {
    loading: 'Loading encryption keys...',
    errorLoading: 'Error loading keys: {error}',
    noKeysFound: 'No Encryption Keys Found',
    noKeysMessage: 'Create a new encrypted message to generate keys.',
    createNewMessage: 'Create New Message',
    importFromBackup: 'Import keys from a backup file',
    yourSavedKeys: 'Your Saved Keys',
    syncing: 'Syncing...',
    syncAllLocal: 'Sync All Local Keys',
    exportTitle: 'Download all keys as a JSON backup file',
    export: 'Export',
    importTitle: 'Restore keys from a backup file',
    import: 'Import',
    createNewKey: 'Create New Key',
    backupHint1: 'Keep a backup:',
    backupHintLink: 'export your keys',
    backupHint2: 'to a file and store it somewhere safe (USB drive, password manager). Losing your keys makes your printed papers unreadable.',
    localKeysSection: 'Local Keys ({count})',
    localKeysDesc: 'These keys are stored in your browser',
    cloudKeysSection: 'Cloud Keys ({count})',
    cloudKeysDesc: 'These keys are saved to your account',
    untitledKey: 'Untitled Key',
    localBadge: 'Local',
    cloudBadge: 'Cloud',
    showKey: 'Show Key',
    hideKey: 'Hide Key',
    saveToCloud: 'Save to Cloud',
    deleting: 'Deleting...',
    delete: 'Delete',
    created: 'Created: {date}',
    storedLocally: 'Stored locally in your browser',
    storedInAccount: 'Stored in your account',
    notBackupFile: 'This file is not a SecurePaper key backup',
    importedKeyName: 'Imported key {date}',
    importedResult: 'Imported {count} key(s)',
    importedNone: 'Imported 0 keys (all keys from the file already exist)',
    readBackupFailed: 'Could not read the backup file',
  },
  cookie: {
    title: 'Your Privacy Choices',
    text: 'We use cookies and similar technologies to enhance site experience, analyze traffic, and understand usage. You can choose to accept all, reject non-essential cookies, or customize your preferences at any time.',
    privacyPolicy: 'Privacy Policy',
    cookieSettings: 'Cookie Settings',
    doNotShare: 'Do Not Share My Personal Information',
    rejectAll: 'Reject All',
    acceptAll: 'Accept All',
    preferencesTitle: 'Cookie Preferences',
    close: 'Close',
    preferencesText: 'Manage how SecurePaper uses cookies and similar technologies. You can update your choices at any time. Essential cookies are always on as they are required for the site to function.',
    essential: 'Essential Cookies',
    essentialDesc: 'Required for core site functionality and security. These cannot be disabled.',
    alwaysActive: 'Always Active',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'Help us understand how visitors use our site so we can improve performance and user experience.',
    functional: 'Functional Cookies',
    functionalDesc: 'Enable enhanced functionality and personalization, such as remembering your preferences.',
    rejectNonEssential: 'Reject Non-Essential',
    savePreferences: 'Save Preferences',
  },
};

export default en;
